/** First-login welcome modal for customer/tenant users.
 *  A simple, dismissible modal with quick links to the key destinations.
 *  Shown once — dismissal is stored per-user via my-settings.onboarding_seen. */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { own } from "@/lib/terminology";
import { PackageSearch, ReceiptText, Store, X, Sparkles } from "lucide-react";

export default function CustomerWelcomeModal({ user }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.requires_legal_acceptance || user?.must_change_password) return;
    let cancelled = false;
    api.get("/config-studio/my-settings")
      .then(({ data }) => {
        if (!cancelled && !(data?.onboarding_seen?.welcome)) setOpen(true);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user?.requires_legal_acceptance, user?.must_change_password]);

  const dismiss = async () => {
    setOpen(false);
    try {
      await api.put("/config-studio/my-settings", { onboarding_seen: { welcome: true } });
    } catch { /* best-effort — modal already closed */ }
  };

  const go = (to) => { dismiss(); navigate(to); };

  if (!open) return null;
  if (user?.requires_legal_acceptance || user?.must_change_password) return null;

  const cards = [
    { to: "/my-orders", icon: PackageSearch, label: own(user, "Orders"),
      desc: "Track every order from placement to delivery", testId: "welcome-link-orders" },
    { to: "/invoices", icon: ReceiptText, label: own(user, "Invoices"),
      desc: "View invoices and keep on top of ageing", testId: "welcome-link-invoices" },
    { to: "/marketplace", icon: Store, label: "Marketplace",
      desc: "Browse and request technology and services", testId: "welcome-link-marketplace" },
  ];

  const firstName = (user?.name || "").split(" ")[0];

  return (
    <div className="fixed inset-0 z-[140] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="customer-welcome-modal" onClick={dismiss}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="relative px-6 pt-6 pb-5 bg-slate-900 text-white">
          <button onClick={dismiss} data-testid="welcome-close"
            className="absolute top-4 right-4 h-8 w-8 grid place-items-center rounded-md text-white/70 hover:text-white hover:bg-white/10">
            <X className="h-4 w-4" />
          </button>
          <div className="inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.14em] uppercase text-emerald-300 mb-2">
            <Sparkles className="h-3.5 w-3.5" /> Welcome to SUNTRONIX ONE
          </div>
          <h2 className="text-xl font-bold leading-snug text-white">
            {firstName ? `Hello ${firstName}, welcome aboard` : "Welcome aboard"}
          </h2>
          <p className="text-[13px] text-white/70 mt-1">
            Here are a few places to get you started. You can reach these anytime from the top navigation.
          </p>
        </div>

        <div className="p-5 space-y-2.5">
          {cards.map((c) => (
            <button key={c.to} onClick={() => go(c.to)} data-testid={c.testId}
              className="w-full text-left flex items-center gap-4 p-3.5 rounded-xl border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/40 transition-colors group">
              <div className="h-11 w-11 shrink-0 rounded-lg bg-slate-900 text-white grid place-items-center group-hover:bg-emerald-700 transition-colors">
                <c.icon className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <div className="text-[14px] font-semibold text-slate-900">{c.label}</div>
                <div className="text-[12px] text-slate-500">{c.desc}</div>
              </div>
            </button>
          ))}
        </div>

        <div className="px-5 pb-5">
          <button onClick={dismiss} data-testid="welcome-get-started"
            className="w-full h-10 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white text-[13px] font-semibold">
            Get started
          </button>
        </div>
      </div>
    </div>
  );
}
